import { useEffect, useRef, useState } from 'react';

/** Below this the bar is always shown. The top of a list is not "scrolled". */
const ALWAYS_SHOWN_ABOVE = 24;

/**
 * Continuous travel in one direction before the bar moves.
 *
 * A per-event threshold was not enough. A thumb flick on a phone fires scroll
 * events every frame, and any single one of them can cross a small threshold
 * — so the bar flipped on the smallest wobble and read as twitchy. These are
 * distances the reader has actually travelled, accumulated across events and
 * reset whenever the direction changes.
 *
 * Coming back is cheaper than going away, because wanting the navigation is
 * a deliberate act and losing it is not.
 */
const HIDE_AFTER = 40;
const SHOW_AFTER = 18;

/**
 * How close to the end counts as the end.
 *
 * Generous on purpose: overscroll bounce on both iOS and Android reports a
 * jittering `scrollTop` at the extremes, which is what made the bar flicker
 * at the bottom of a list. Inside this band it is simply always shown, which
 * is also the right behaviour — there is nothing further to read, so give the
 * navigation back.
 */
const BOTTOM_ZONE = 48;

/**
 * Hide the bar while reading down, bring it back on the way up.
 *
 * Listening on `document` in the CAPTURE phase rather than on one element,
 * because scroll events do not bubble and every screen owns its own scroller
 * — Today, Jatra, Khata and আমি each have their own `overflow-y-auto`. One
 * capturing listener sees all of them and keeps working when a screen is
 * added, which a ref threaded down through four screens would not.
 *
 * Any pointer down brings it back, anywhere on the page. That covers the
 * obvious gesture — tapping empty space to get the navigation — and also
 * means a student who taps a habit row never finds the bar missing when
 * they look for it.
 */
export function useHideOnScroll(): boolean {
  const [hidden, setHidden] = useState(false);

  // Per-scroller, because the screens do not share one. A single shared
  // "last position" would read the new screen's 0 as a huge upward scroll
  // every time the tab changed.
  const positions = useRef(new WeakMap<Element, number>());

  /** Signed distance travelled since the bar last moved. */
  const travel = useRef(0);

  useEffect(() => {
    const settle = (next: boolean) => {
      travel.current = 0;
      setHidden(next);
    };

    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // A horizontal scroller — the wall chart, the year map — fires scroll
      // events too, and has nothing to say about reading down a page.
      if (target.scrollHeight <= target.clientHeight) return;

      const top = target.scrollTop;
      const previous = positions.current.get(target) ?? 0;
      const delta = top - previous;
      if (delta === 0) return;
      positions.current.set(target, top);

      // Both ends of the list are always-shown zones, so bounce at either
      // extreme cannot start a fight with the accumulator.
      if (top <= ALWAYS_SHOWN_ABOVE) return settle(false);
      if (top + target.clientHeight >= target.scrollHeight - BOTTOM_ZONE) return settle(false);

      // A change of direction starts the count again — otherwise a long read
      // downward leaves a credit that hides the bar on the next flick up.
      travel.current = travel.current > 0 === delta > 0 ? travel.current + delta : delta;

      if (travel.current >= HIDE_AFTER) settle(true);
      else if (travel.current <= -SHOW_AFTER) settle(false);
    };

    const reveal = () => settle(false);

    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('pointerdown', reveal, { capture: true, passive: true });

    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('pointerdown', reveal, { capture: true });
    };
  }, []);

  return hidden;
}
